import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import useApi from '../hooks/useApi';

const Notification = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [classNotifications, setClassNotifications] = useState([]);
  const hasLoadedRef = useRef(false);
  const { bearerAuth } = useApi();

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await bearerAuth('user/dashboard');
      const data = response?.data?.data || {};
      setClassNotifications(Array.isArray(data.class_notifications) ? data.class_notifications : []);
    } catch (_) {
      setError('Failed to load notifications');
      setClassNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [bearerAuth]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadNotifications();
  }, [loadNotifications]);

  const resolveBadgeVariant = (severity) => {
    if (severity === 'success') return 'success';
    if (severity === 'danger') return 'danger';
    if (severity === 'warning') return 'warning';
    return 'primary';
  };

  return (
    <div className="py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0 d-flex align-items-center gap-2">
          <FaBell className="text-primary" />
          Notifications
        </h2>
        <Button variant="outline-primary" size="sm" onClick={loadNotifications} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span>{error}</span>
          <Button variant="outline-danger" size="sm" onClick={loadNotifications}>
            Retry
          </Button>
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="text-muted mt-3">Loading notifications...</div>
            </div>
          ) : classNotifications.length === 0 ? (
            <div className="text-muted">No class notifications yet.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {classNotifications.map((item, index) => (
                <div
                  key={`notification-${index}`}
                  className="p-3 rounded border d-flex align-items-start justify-content-between gap-3"
                  style={{
                    borderLeft: `4px solid ${
                      item.severity === 'success'
                        ? '#198754'
                        : item.severity === 'danger'
                          ? '#dc3545'
                          : item.severity === 'warning'
                            ? '#ffc107'
                            : '#0d6efd'
                    }`,
                  }}
                >
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-1">{item.title || 'Class update'}</div>
                    <div className="text-muted small">
                      {item.subtitle || 'Your class status has been updated.'}
                    </div>
                    {item.remarks ? (
                      <div className="small mt-2">
                        <span className="text-muted">Remarks:</span> {item.remarks}
                      </div>
                    ) : null}
                  </div>

                  <div className="text-end">
                    <Badge bg={resolveBadgeVariant(item.severity)} className="mb-2 text-capitalize">
                      {item.status || 'info'}
                    </Badge>
                    <div className="small text-muted">
                      {item.created_at ? new Date(item.created_at).toLocaleString() : item.date || 'Just now'}
                    </div>
                    <Button as={Link} to="/dashboard/schedule" size="sm" variant="outline-primary" className="mt-2">
                      View Class
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Notification;
